<template>
    <div class="wrapper">
        <h1>The language learning search</h1>
        <h5>by Ciprian Corobca</h5>
        <div id="title-target-separator"></div>
        <h3 id="target-lbl">Target language</h3>
        <select v-model="targetLanguage" :class="[selectErr ? 'errored' : '']" @click="clearSelectErr()">
            <option v-for="language in availableLanguages" :key="language.code" :value="language.name">{{ language.name }}</option>
        </select>
        <div id="target-scrollable-separator"></div>
        <h3 id="your-lbl">Your known languages</h3>
        <div class="scroll-container" :key="resetKey">
            <language-picker v-for="(language,index) in languageArray" :key="index" :available="availableLanguages" :index="index" :unsaved="unsaved" @saved="langSaved()"/>
            <button id="add" title="Click to add a new language" @click="addLanguage()" :class="[addErr ? 'errored' : '']" :disabled="isInDraft">+</button>
        </div>
        <div id="scroll-submit-separator"></div>
        <button id="reset-form" @click="resetSearch()">Reset</button>
        <button id="submit-form" @click="getResults()">Submit</button>
    </div>
</template>

<script setup>
    import { computed, ref } from 'vue';
    import { languageStore } from '@/stores/languages';
    import LanguagePicker from './LanguagePicker.vue';
    import router from '@/router';

    var languageArray = languageStore().languages;
    var isInDraft = computed(() => languageStore().isInDraft);

    var availableLanguages = ref([
        { code:'sel',name:"Select"},
        { code: 'sq', name: 'Albanian' },
        { code: 'am', name: 'Amharic' },
        { code: 'ar', name: 'Arabic' },
        { code: 'hy', name: 'Armenian' },
        { code: 'id', name: 'Bahasa Indonesia' },
        { code: 'eu', name:'Basque'},
        { code: 'bn', name: 'Bengali' },
        { code: 'bg', name: 'Bulgarian' },
        { code: 'yue', name: 'Cantonese' },
        { code: 'hr', name:'Croatian'},
        { code: 'cz', name: 'Czech' },
        { code: 'nl', name: 'Dutch' },
        { code: 'en', name: 'English' },
        { code: 'fa', name: 'Farsi' },
        { code: 'fi', name: 'Finnish' },
        { code: 'fr', name: 'French' },
        { code: 'ka', name: 'Georgian' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'he', name:'Hebrew'},
        { code: 'hi', name: 'Hindi' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'ga', name:'Irish'},
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'kk', name: 'Kazakh' },
        { code: 'ko', name: 'Korean' },
        { code: 'ku', name: 'Kurdish' },
        { code: 'la', name:'Latin'},
        { code: 'lt', name: 'Lithuanian' },
        { code: 'zh', name: 'Mandarin' },
        { code: 'mn', name: 'Mongolian' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'pa', name: 'Punjabi' },
        { code: 'ro', name: 'Romanian' },
        { code: 'ru', name: 'Russian' },
        { code: 'sr', name: 'Serbian' },
        { code: 'sk', name: 'Slovak' },
        { code: 'es', name: 'Spanish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'tl', name: 'Tagalog' },
        { code: 'ta', name: 'Tamil' },
        { code: 'th', name: 'Thai' },
        { code: 'tr', name: 'Turkish' },
        { code: 'ua', name: 'Ukrainian' },
        { code: 'ur', name: 'Urdu' },
        { code: 'vi', name: 'Vietnamese' },
        { code:'cy', name: 'Welsh'}
    ]);

    var targetLanguage = ref("Select");
    var resetKey = ref(0);
    var selectErr = ref(false),addErr = ref(false),unsaved = ref(false);

    const addLanguage = () => {
        languageStore().addLanguage();
        addErr.value = false;
    }

    const resetSearch = () => {
        resetKey.value++;
        targetLanguage.value = "Select";
        languageStore().resetSearch();
        languageArray = languageStore().languages;
        selectErr.value = false;
        addErr.value = false;
        unsaved.value = false;
    }

    const clearSelectErr = () => {selectErr.value = false}

    const langSaved = () => {unsaved.value = false}

    const getResults = async () => {
        if(targetLanguage.value != "Select" && languageArray.length > 0 && !isInDraft.value)
        {
            try 
            {
                await languageStore().GetResults(targetLanguage.value);
                router.push('/results');
            } 
            catch (error) 
            {
                console.error('Action failed:', error);
            }
        }

        else if(targetLanguage.value == "Select" && languageArray.length > 0 && !isInDraft.value)
        {
            selectErr.value = true;
        }

        else if(targetLanguage.value != "Select" && languageArray.length == 0)
        {
            addErr.value = true;
        }

        else if(targetLanguage.value != "Select" && languageArray.length > 0 && isInDraft.value)
        {
            unsaved.value = true;
        }

        else if(targetLanguage.value == "Select" && languageArray.length > 0 && isInDraft.value)
        {
            selectErr.value = true;
            unsaved.value = true;
        }

        else if(targetLanguage.value == "Select" && languageArray.length == 0)
        {
            selectErr.value = true;
            addErr.value = true;
        }
    }
</script>

<style scoped>
    h1,h3,h5,select,button
    {
        font-family: Playfair Display;
    }

    h1,h3,h5
    {
        margin:0;
        cursor:default;
    }

    .wrapper
    {
        width:min(400px,100vw);
        height:100dvh;
        margin-inline: auto;
        display:grid;
        grid-template-rows: 35px 20px 1px 24px 40px 1px 24px 1fr 1px 60px;
        grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
        row-gap: 3px;
    }

    .scroll-container
    {
        display:flex;
        flex-direction: column;
        width:100%;
        height:100%;
        align-items: center;
        overflow: scroll;
        gap:10px;
    }

    .scroll-container::-webkit-scrollbar
    {
        width:0;
        background: transparent;
    }

    div#title-target-separator
    {
        grid-area: 3 / 1 / 4 / -1;
        border:1px solid rgba(0, 0, 0, 0.481);
        width:90%;
        justify-self: center;
    }

    div#target-scrollable-separator
    {
        grid-area: 6 / 1 / 7 / -1;
        border:1px solid rgba(0, 0, 0, 0.481);
        width:90%;
        justify-self: center;
    }

    div#scroll-submit-separator
    {
        grid-area: 9 / 1 / 10 / -1;
        border:1px solid rgba(0, 0, 0, 0.481);
        width:90%;
        justify-self: center;
    }

    h1
    {
        grid-area: 1 / 1 / 2 / -1;
        font-size: 24px;
        justify-self: center;
    }

    h5
    {
        grid-area: 2 / 1 / 3 / -1;
        font-size: 14px;
        justify-self: center;
    }

    h3#target-lbl
    {
        grid-area: 4 / 3 / 5 / -3;
    }

    h3#your-lbl
    {
        grid-area: 6 / 3 / 7 / -3;
    }

    h3
    {
        font-size: 16px;
    }

    select
    {
        grid-area: 5 / 3 / 6 / -3;
        font-size: 20px;
    }

    .scroll-container
    {
        grid-area: 8 / 1 / 9 / -1;
    }

    #reset-form
    {
        grid-area: 10 / 1 / -1 / 6;
    }

    #submit-form
    {
        grid-area: 10 / 6 / -1 / -1;
    }

    #submit-form,#reset-form
    {
        justify-self: center;
        align-self: center;
        font-size: 28px;
        width:120px;
        border:3px solid #5a504c;
        border-radius: 8px;
        font-weight: bold;
        cursor: pointer;
    }

    #reset-form
    {
        background:transparent;
        color:#5a504c;
    }

    #submit-form
    {
        background: #5a504c;
        color:#fbdfca;
    }

    #reset-form:hover 
    {
        background: #5a504c6e;
    }

    #submit-form:hover
    {
        text-decoration: underline;
    }

    #add
    {
        font-size: 40px;
        width:90%;
        border-radius: 40px;
        border:0;
        background: #fff8f2;
        color:black;
        font-weight: 600;
        cursor: pointer;
    }

    #add:disabled
    {
        color:rgba(0, 0, 0, 0.41);
        cursor:not-allowed;
    }

    #add:hover
    {
        color:#5a504c;
    }

    #add:disabled:hover
    {
        color:rgba(0, 0, 0, 0.41);
    }

    select
    {
        cursor:pointer;
        outline:none;
        background: #be7c5c;
        font-size: 24px;
        text-decoration: underline;
        color:#fff8f2;
        border:none;
        border-radius: 6px;
        text-indent: 8px;
        -webkit-appearance: none; 
        -moz-appearance: none;   
        appearance: none;
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path d="M7 10l5 5 5-5z"/><path d="M0 0h24v24H0z" fill="none"/></svg>');
        background-repeat: no-repeat;
        background-position: right 0 center;
        background-size: 2em; 
        padding-right: 1em;
    }

    .errored
    {
        border:3px dashed red !important; 
    }

    @media (max-width:330px)
    {
        .wrapper
        {
            grid-template-rows: 24px 20px 1px 24px 40px 1px 24px 1fr 1px 60px;
        }

        h1
        {
            font-size: 18px;
        }

        select
        {
            grid-area: 5 / 2 / 6 / -2;
        }
    }

    @media (max-width:280px)
    {
        .wrapper
        {
            grid-template-rows: 24px 14px 1px 16px 40px 1px 16px 1fr 1px 60px;
        }

        h3
        {
            font-size:13px;
        }

        h5
        {
            font-size: 10px;
        }
    }

    @media (max-width:250px)
    {
        .wrapper
        {
            grid-template-rows: 18px 14px 1px 16px 30px 1px 16px 1fr 1px 40px;
        }

        h1
        {
            font-size: 15px;
        }

        select
        {
            font-size: 20px;
            text-indent: 4px;
        }

        #submit-form,#reset-form
        {
            font-size: 20px;
            width:80px;
            border:2px solid #5a504c;
            border-radius: 5px;
        }

        #add
        {
            font-size: 25px;
            border-radius: 25px;
        }
    }

    @media (max-width:230px)
    {
        .wrapper
        {
            grid-template-rows: 16px 14px 1px 16px 30px 1px 16px 1fr 1px 40px;
        }

        h1,h3
        {
            font-size: 12px;
        }

        h3#target-lbl
        {
            grid-area: 4 / 2 / 5 / -2;
        }

        h3#your-lbl
        {
            grid-area: 6 / 2 / 7 / -2;
        }
    }

    @media (max-height:600px)
    {
        .wrapper
        {
            grid-template-rows: 24px 20px 1px 24px 40px 1px 24px 1fr 1px 60px;
        }

        h1
        {
            font-size: 18px;
        }
    }

    @media (max-height:540px)
    {
        .wrapper
        {
            grid-template-rows: 22px 14px 1px 16px 30px 1px 16px 1fr 1px 40px;
        }

        select
        {
            font-size: 20px;
            text-indent: 4px;
        }

        h3
        {
            font-size:13px;
        }

        h5
        {
            font-size: 10px;
        }

        #add
        {
            font-size: 25px;
            border-radius: 25px;
        }

        #submit-form,#reset-form
        {
            font-size: 20px;
            width:80px;
            border:2px solid #5a504c;
            border-radius: 5px;
        }
    }

    @media (max-height:450px)
    {
        .wrapper
        {
            grid-template-rows: 16px 14px 1px 16px 30px 1px 16px 1fr 1px 40px;
        }

        h1,h3
        {
            font-size: 12px;
        }
    }

    @media (max-height:380px)
    {
        .wrapper
        {
            grid-template-rows: 12px 10px 1px 12px 30px 1px 12px 1fr 1px 40px;
        }

        h1,h3
        {
            font-size: 10px;
        }

        h5
        {
            font-size: 8px;
        }
    }
</style>